import {ChevronRightIcon} from '@heroicons/react/24/solid';
import {useParams} from 'react-router-dom';
import {useQueryParam} from 'use-query-params';

import LoadingSpinner from 'src/components/LoadingSpinner';
import PageContainer from 'src/components/PageContainer';
import PageTitle from 'src/components/PageTitle';
import {TimezoneProvider} from 'src/components/TimezoneContext';
import {useTranslations} from 'src/components/TranslationsContext';
import {LoggedInAuth} from 'src/hooks/useAuth';
import {useFlowContributingAreas} from 'src/hooks/useFlowContributingAreas';
import {useProject} from 'src/hooks/useProject';
import {ApiUserProfile} from 'src/lib/api/auth';
import {Project, Site} from 'src/lib/api/project';

import {ProjectSiteSection} from './Site';
import {SiteSelect} from './SiteSelect';
import TimeZoneToggle from './TimeZoneToggle';

type ProjectPageProps = {
  auth: LoggedInAuth;
  currentUser: ApiUserProfile;
};

type ProjectPageParams = {
  projectId: Project['id'];
};

export default function ProjectPage({auth, currentUser}: ProjectPageProps) {
  const {projectId} = useParams() as ProjectPageParams;
  const {project} = useProject(projectId);
  const {flowContributingAreas} = useFlowContributingAreas(projectId);
  const {t} = useTranslations();

  const [selectedSiteId, setSelectedSiteId] = useQueryParam<Site['id'] | null>('siteId');

  if (!project) {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <LoadingSpinner />
      </div>
    );
  }

  const selectedSite = project.sites?.find((site) => site.id === selectedSiteId) || null;

  return (
    <PageContainer>
      <TimezoneProvider currentUser={currentUser}>
        <PageTitle
          pageStack={[
            {title: t('projects'), to: '/:currentOrgIdPrefix/projects'},
            {
              title: project.name,
              to: '/:currentOrgIdPrefix/projects/:projectId',
            },
          ]}
          stickyTitle={selectedSite ? `${project.name} › ${selectedSite.name}` : project.name}
          tag={project.isHidden ? t('internal') : null}
          additionalContent={
            selectedSiteId &&
            selectedSite && (
              <div className="flex items-center justify-between flex-1">
                <div className="flex items-center">
                  <ChevronRightIcon className="h-6 w-6 pt-1" />
                  <SiteSelect
                    selectedSiteId={selectedSiteId}
                    setSelectedSiteId={setSelectedSiteId}
                    project={project}
                  />
                </div>
                <TimeZoneToggle selectedSite={selectedSite} />
              </div>
            )
          }
        />
        <ProjectSiteSection
          auth={auth}
          project={project}
          flowContributingAreas={flowContributingAreas}
          currentUser={currentUser}
          selectedSiteId={selectedSiteId}
          setSelectedSiteId={setSelectedSiteId}
        />
      </TimezoneProvider>
    </PageContainer>
  );
}
